import React from 'react';
import {
  Grid,
  Select,
  ISelectOption,
  NumberInputStyled,
  FormCell,
  SpruceField,
  ISpruceFieldProps,
  getColoredCurativeGradeOptions,
  MultipleSelectField,
  SelectField,
  TextField,
  DateInput,
  getCurativeGradeDot,
} from 'ui-toolkit';
import { NumberFormatValues } from 'react-number-format';
import { DateTime } from 'luxon';
import { eUSAState, StatesLabelValue } from '../../ui-toolkit/enums/states';
import { DisplayTransactionCategory, eTransactionCategory } from '../../ui-toolkit/enums/eTransactionType';
import { eExternalOrderStatus } from '../../ui-toolkit/enums/eExternalOrderStatus';
import { prettyInternalOrderStatus } from '../../ui-toolkit/enums/eInternalOrderStatus';
import { DefaultOrdersSearchForm } from '../../ui-toolkit/orders/searchOrder/components/DefaultOrdersSearchForm';
import { ICountyData } from '../../ui-toolkit/orders/searchOrder/types/ICounty';

interface IAdvancedOrdersSearchFormProps {
  lenders: Array<ISelectOption<string>>;
  counties: ICountyData[];
  onStateSelect: (state: eUSAState | null) => void;
}

export const AdvancedOrdersSearchForm: React.FC<IAdvancedOrdersSearchFormProps> = ({
  lenders,
  counties,
  onStateSelect,
}) => {
  const statusOptions: ISelectOption[] = Object.values(eExternalOrderStatus).map((status) => ({
    label: prettyInternalOrderStatus[status],
    value: status,
  }));
  const stateFilterOptions = (option: ISelectOption<string>, value: string) => {
    return option.value.toLowerCase().indexOf((value || '').toLowerCase()) >= 0;
  };
  const countiesOptions: ISelectOption[] = counties.map((county) => ({
    label: county.name,
    value: county.id,
  }));
  const transactionCategoryOptions: ISelectOption[] = Object.values(eTransactionCategory).map((category) => ({
    label: DisplayTransactionCategory[category],
    value: category,
  }));

  const curativeGradeOptions = getColoredCurativeGradeOptions();

  return (
    <Grid>
      <DefaultOrdersSearchForm />
      <Grid columnCount={5}>
        <TextField name={'city'} label={'City'} placeholder={'Search for a city'} />
        <SpruceField name={'state'} inputType={'select'}>
          {({ field }: ISpruceFieldProps) => (
            <Select
              {...field}
              onChange={(value: any) => {
                field.onChange(value);
                onStateSelect(value?.value ?? null);
              }}
              filterOption={stateFilterOptions}
              value={StatesLabelValue.find((state) => state.value === field.value) || null}
              options={StatesLabelValue}
              isClearable={true}
              label={'State'}
              placeholder={'Select a State'}
            />
          )}
        </SpruceField>
        <SpruceField name={'countyId'} inputType={'select'}>
          {({ field, formik }: ISpruceFieldProps) => (
            <Select
              {...field}
              isDisabled={!formik.values.state}
              hintText={formik.values.state ? null : 'Select a State'}
              value={countiesOptions.find((state) => state.value === field.value) || null}
              options={countiesOptions}
              isClearable={true}
              label={'County'}
              placeholder={'Select a county'}
            />
          )}
        </SpruceField>
        <SpruceField name={'zipCode'}>
          {({ field, formik }: ISpruceFieldProps) => (
            <FormCell errorText={field.errorText} label={`Zip Code`}>
              <NumberInputStyled
                errorText={field.errorText}
                showErrorHighlight={!!field.errorText}
                allowEmptyFormatting={false}
                value={field.value || undefined}
                format={`#####`}
                type={'tel'}
                placeholder={`12345`}
                onBlur={() => formik.setFieldTouched(field.name)}
                onValueChange={({ value }: NumberFormatValues) => {
                  formik.setFieldValue(field.name, value ? value : null);
                }}
              />
            </FormCell>
          )}
        </SpruceField>
        <MultipleSelectField
          name={'lenderCompanyIds'}
          options={lenders}
          isClearable
          label={'Lender'}
          placeholder={'Search for a Lender'}
        />
      </Grid>
      <Grid columnCount={5}>
        <TextField name={'loanNumber'} label={'Loan ID'} placeholder={'Search by Loan ID'} />
        <TextField
          name={'externalPartyTransactionId'}
          label={'Transaction ID'}
          placeholder={'Search by Transaction ID'}
        />
        <SelectField
          name={'transactionCategory'}
          label={'Transaction Category'}
          options={transactionCategoryOptions}
          isClearable
          placeholder={'Select'}
        />
        <MultipleSelectField
          name={'externalStatuses'}
          options={statusOptions}
          isClearable={true}
          label={'Status(es)'}
          placeholder={'Select a Status(es)'}
        />
        <MultipleSelectField
          name={'curativeGrades'}
          isClearable
          options={curativeGradeOptions}
          label={'Curative Grade(s)'}
          placeholder={'Curative Grade(s)'}
          styles={{
            option: (baseStyles, { data }) => {
              return {
                ...baseStyles,
                ...getCurativeGradeDot(data),
              };
            },
          }}
        />
      </Grid>
      <Grid columnCount={2}>
        <SpruceField name={'createdAtStart'}>
          {({ field, formik }: ISpruceFieldProps) => (
            <DateInput
              {...field}
              label="Created At"
              placeholder="MM/DD/YYYY"
              onChange={(value) => {
                field.onChange(value.value);
                formik.setFieldValue(
                  'createdAtEnd',
                  value.value ? DateTime.fromISO(value.value).plus({ day: 1 }).toISODate() : null
                );
              }}
            />
          )}
        </SpruceField>
        <SpruceField name={'closingDateStart'}>
          {({ field, formik }: ISpruceFieldProps) => (
            <DateInput
              {...field}
              label="Closing Date"
              placeholder="MM/DD/YYYY"
              onChange={(value) => {
                field.onChange(value.value);
                formik.setFieldValue(
                  'closingDateEnd',
                  value.value ? DateTime.fromISO(value.value).plus({ day: 1 }).toISODate() : null
                );
              }}
            />
          )}
        </SpruceField>
      </Grid>
    </Grid>
  );
};
